/**
 * Capacitor utilities for native app functionality
 * This file provides platform detection and native feature wrappers
 */

import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { Keyboard } from '@capacitor/keyboard';
import { Preferences } from '@capacitor/preferences';

// ============== PLATFORM DETECTION ==============

/**
 * Check if running in a native Capacitor app
 */
export const isNativeApp = (): boolean => {
  return Capacitor.isNativePlatform();
};

/**
 * Check if running on Android
 */
export const isAndroid = (): boolean => {
  return Capacitor.getPlatform() === 'android';
};

/**
 * Check if running on iOS
 */
export const isIOS = (): boolean => {
  return Capacitor.getPlatform() === 'ios';
};

/**
 * Check if running in web browser
 */
export const isWeb = (): boolean => {
  return Capacitor.getPlatform() === 'web';
};

// ============== BROWSER / OAUTH ==============

/**
 * Open URL in browser - uses native browser on mobile, window.location on web
 * This is essential for OAuth flows that need to return to the app
 */
export const openBrowser = async (url: string): Promise<void> => {
  if (isNativeApp()) {
    // Use Capacitor Browser plugin for in-app browser
    await Browser.open({ url });
  } else {
    // Fallback to regular redirect on web
    window.location.href = url;
  }
};

/**
 * Close the in-app browser (only works on native)
 */
export const closeBrowser = async (): Promise<void> => {
  if (isNativeApp()) {
    await Browser.close();
  }
};

/**
 * Listen for browser closed events (for OAuth completion)
 */
export const onBrowserClosed = (callback: () => void): (() => void) => {
  if (isNativeApp()) {
    const listener = Browser.addListener('browserFinished', callback);
    return () => listener.remove();
  }
  return () => {}; // No-op on web
};

// ============== DEEP LINKS / APP URLS ==============

/**
 * Listen for app URL open events (deep links)
 * This is used for OAuth callback handling on native apps
 */
export const onAppUrlOpen = (
  callback: (url: string) => void
): (() => void) => {
  if (isNativeApp()) {
    const listener = App.addListener('appUrlOpen', (event) => {
      callback(event.url);
    });
    return () => listener.remove();
  }
  return () => {}; // No-op on web
};

/**
 * Get the URL that was used to launch the app (if any)
 */
export const getLaunchUrl = async (): Promise<string | null> => {
  if (isNativeApp()) {
    const result = await App.getLaunchUrl();
    return result?.url || null;
  }
  return null;
};

// ============== STATUS BAR ==============

/**
 * Set status bar style for dark/light mode
 */
export const setStatusBarStyle = async (isDark: boolean): Promise<void> => {
  if (isNativeApp()) {
    try {
      await StatusBar.setStyle({
        style: isDark ? Style.Dark : Style.Light,
      });

      if (isAndroid()) {
        await StatusBar.setBackgroundColor({
          color: isDark ? '#1e293b' : '#f6f1e8', // Slate-800 or day theme
        });
      }
    } catch (e) {
      console.warn('StatusBar not available:', e);
    }
  }
};

/**
 * Hide status bar (for immersive mode)
 */
export const hideStatusBar = async (): Promise<void> => {
  if (isNativeApp()) {
    try {
      await StatusBar.hide();
    } catch (e) {
      console.warn('StatusBar not available:', e);
    }
  }
};

/**
 * Show status bar
 */
export const showStatusBar = async (): Promise<void> => {
  if (isNativeApp()) {
    try {
      await StatusBar.show();
    } catch (e) {
      console.warn('StatusBar not available:', e);
    }
  }
};

// ============== SPLASH SCREEN ==============

/**
 * Hide the splash screen (call after app is ready)
 */
export const hideSplashScreen = async (): Promise<void> => {
  if (isNativeApp()) {
    try {
      await SplashScreen.hide();
    } catch (e) {
      console.warn('SplashScreen not available:', e);
    }
  }
};

// ============== KEYBOARD ==============

/**
 * Hide the keyboard
 */
export const hideKeyboard = async (): Promise<void> => {
  if (isNativeApp()) {
    try {
      await Keyboard.hide();
    } catch (e) {
      console.warn('Keyboard not available:', e);
    }
  }
};

/**
 * Listen for keyboard show events
 */
export const onKeyboardShow = (
  callback: (info: { keyboardHeight: number }) => void
): (() => void) => {
  if (isNativeApp()) {
    const listener = Keyboard.addListener('keyboardWillShow', (info) => {
      callback({ keyboardHeight: info.keyboardHeight });
    });
    return () => listener.remove();
  }
  return () => {};
};

/**
 * Listen for keyboard hide events
 */
export const onKeyboardHide = (callback: () => void): (() => void) => {
  if (isNativeApp()) {
    const listener = Keyboard.addListener('keyboardWillHide', callback);
    return () => listener.remove();
  }
  return () => {};
};

// ============== PREFERENCES (Alternative to localStorage) ==============

/**
 * Set a preference value (works better on native than localStorage)
 */
export const setPreference = async (
  key: string,
  value: string
): Promise<void> => {
  if (isNativeApp()) {
    await Preferences.set({ key, value });
  } else {
    localStorage.setItem(key, value);
  }
};

/**
 * Get a preference value
 */
export const getPreference = async (key: string): Promise<string | null> => {
  if (isNativeApp()) {
    const result = await Preferences.get({ key });
    return result.value;
  } else {
    return localStorage.getItem(key);
  }
};

/**
 * Remove a preference
 */
export const removePreference = async (key: string): Promise<void> => {
  if (isNativeApp()) {
    await Preferences.remove({ key });
  } else {
    localStorage.removeItem(key);
  }
};

// ============== APP LIFECYCLE ==============

/**
 * Listen for app state changes (background/foreground)
 */
export const onAppStateChange = (
  callback: (state: { isActive: boolean }) => void
): (() => void) => {
  if (isNativeApp()) {
    const listener = App.addListener('appStateChange', callback);
    return () => listener.remove();
  }
  return () => {};
};

/**
 * Listen for back button press (Android only)
 */
export const onBackButton = (callback: () => void): (() => void) => {
  if (isAndroid()) {
    const listener = App.addListener('backButton', callback);
    return () => listener.remove();
  }
  return () => {};
};

/**
 * Exit the app (Android only, iOS doesn't allow programmatic exit)
 */
export const exitApp = (): void => {
  if (isAndroid()) {
    App.exitApp();
  }
};

// ============== INITIALIZATION ==============

/**
 * Initialize Capacitor plugins on app startup
 * Call this in your main App component's useEffect
 */
export const initializeCapacitor = async (): Promise<void> => {
  if (!isNativeApp()) {
    console.log('Running in web mode - Capacitor features disabled');
    return;
  }

  console.log(`Running on ${Capacitor.getPlatform()} platform`);

  // Set initial status bar style
  await setStatusBarStyle(true); // Default to dark mode

  // Hide splash screen after a small delay to ensure app is rendered
  setTimeout(async () => {
    await hideSplashScreen();
  }, 500);
};
